import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { AppContext } from '../../contexts';
import { useApiService, useContextService } from '../../services';
import { InternetUserType } from '../../types/enum/user-type';
import { PageUrl } from '../../util/app-config';
import './login-v2.css';

function LoginV2() {
  const navigate = useNavigate();
  const { resetAppContext } = useContextService();
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const role = appContext.role;
  const { getAlertMessages } = useApiService();

  /* ---- Navigation handlers (unchanged from login.tsx) ---- */

  const spLoginOnClickHandler = () => {
    role.internetUserType = InternetUserType.FoodHandler;
    navigate(`${PageUrl.MILogin}?ut=fh&lt=mi`);
  };

  const onClickBusinessOwner = () => {
    role.internetUserType = InternetUserType.BusinessOwner;
    navigate(`${PageUrl.CPLogin}?ut=bo&lt=cp`);
  };

  const onClickTrainingProvider = () => {
    role.internetUserType = InternetUserType.TrainingProvider;
    navigate(`${PageUrl.CPLogin}?ut=tp&lt=cp`);
  };

  /* ---- Bootstrap / alert message (unchanged from login.tsx) ---- */

  useEffect(() => {
    const getMessage = async () => {
      const msgList = await getAlertMessages();
      if (msgList !== null) {
        const endDate = moment(msgList[0].toDate);
        const currentDate = moment();
        if (currentDate <= endDate) {
          setAlertMessage(msgList[0].message);
        }
      }
    };
    if (!isLoading) {
      resetAppContext();
      setIsLoading(true);
      getMessage();
    }
  }, [isLoading]);

  const getBaseUrl = () => {
    const env = process.env.REACT_APP_ENVIRONMENT;
    if (env === 'Dev' || env === 'Dev1') return 'https://localhost:44354';
    if (env === 'PROD') return 'https://fhd2hub.sfa.gov.sg';
    return 'https://fhd2hub-uat.sfa.gov.sg';
  };

  return (
    <div className="lv2-page">
      <Helmet>
        <link rel="canonical" href={`${getBaseUrl()}/`} />
      </Helmet>

      {/* ---- Hero ---- */}
      <section className="lv2-hero">
        {/* Left: text content */}
        <div className="lv2-hero__content">
          <img
            src="/ClientApp/build/Images/SFA_Logo_Transparent.png"
            alt="Singapore Food Agency Logo"
            className="lv2-hero__logo"
          />
          <p className="lv2-hero__welcome">Welcome to</p>

          <div className="lv2-hero__title">
            <span className="lv2-hero__title--orange">SFA's</span> Food Handlers
            <span style={{ display: 'block', margin: '-20px 0 0 0' }}></span>
            <span className="lv2-hero__title--orange">Digital Data Hub</span>
          </div>
          <div className="lv2-hero__sub">(FHD2Hub)</div>

          {/* Text in the middle of screen */}
          <p className="lv2-hero__desc">
            The Singapore Food Agency is a statutory board under the Ministry of
            Sustainability and the Environment that oversees food safety and
            security in Singapore.
          </p>
        </div>

        {/* Right: two staggered hero photos */}
        <div className="lv2-hero__images">
          <img
            src="/ClientApp/build/Images/BigImage_v2.jpg"
            alt="Food establishment interior"
            className="lv2-hero__img lv2-hero__img--top"
          />
          <img
            src="/ClientApp/build/Images/SmallImage_v2.jpg"
            alt="Food establishment interior"
            className="lv2-hero__img lv2-hero__img--bottom"
          />
        </div>
      </section>

      {/* ---- Role selection cards ---- */}
      <section className="lv2-cards">
        <div className="lv2-cards__row">

          {/* Card 1 – Food Handler / Meat Handler */}
          <button className="lv2-card" onClick={spLoginOnClickHandler}>
            <img
              src="/ClientApp/build/Images/FoodHandler_icon.png"
              alt="Food Handler / Meat Handler"
              className="lv2-card__icon"
            />
            <div className="lv2-card__text">
              <span className="lv2-card__label">I am a</span>
              <span className="lv2-card__title">Food /{'\u00A0'}Meat Handler</span>
            </div>
          </button>

          {/* Card 2 – Business Owner */}
          <button className="lv2-card" onClick={onClickBusinessOwner}>
            <img
              src="/ClientApp/build/Images/BusinessOwner_icon.png"
              alt="Business Owner"
              className="lv2-card__icon"
            />
            <div className="lv2-card__text">
              <span className="lv2-card__label">I am a</span>
              <span className="lv2-card__title">Business Owner</span>
            </div>
          </button>

          {/* Card 3 – Training Provider */}
          <button className="lv2-card" onClick={onClickTrainingProvider}>
            <img
              src="/ClientApp/build/Images/TrainingProvider_icon.png"
              alt="Training Provider"
              className="lv2-card__icon"
            />
            <div className="lv2-card__text">
              <span className="lv2-card__label">For</span>
              <span className="lv2-card__title">Training Provider</span>
            </div>
          </button>

        </div>
      </section>

      {/* Bottom text */}
      <section className="lv2-footer-desc">
        <p>
          The Food Handlers Digital Data Hub is SFA's central repository for all
          food handler-related data. It seamlessly connects Approved Training
          Organisations, Food Business Owners and Food Handlers on a single
          platform, enabling food business owners to easily verify the
          certification status of their staff. Food handlers can also access the
          FHD2Hub to view their Food Safety Course records, making the process
          simple, convenient, and reliable.
        </p>
      </section>
    </div>
  );
}

export default LoginV2;
